/**
 * This reducer function handles all the actions related to the top-level objects.
 * @param {Object} state - A portion of the overall Redux state, specifically the
 *      storeIgua.<%=names.camel%> object, representing the current data.
 * @param {Object} action - Action object containing type and other values
 * @return {*} current state or array of language objects
 */

import initialState from '../initialstate';
import {
	GET_RNH_NOTE_DETAIL,
	OPEN_RNH_NOTE_DETAIL_MODAL,
	CLEAR_RNH_NOTE_DETAIL,
	SET_RNH_SELECTED_NOTE_ID_RESET_CONTEXT,
	GET_RNH_NOTE_REPLIES,
	POST_RNH_NEW_NOTE,
	POST_RNH_NOTE_REPLY,
	UPDATE_RNH_NOTE_INFO,
	UPDATE_RNH_NOTE_DETAIL,
	DELETE_RNH_NOTE_DETAIL,
	DELETE_RNH_PARENT_NOTE,
	GET_FORM_BODY_COMMENT,
	ARES_GET_DOCUMENT_BY_ID
} from '../../actions/actiontypes';

import Utility from '../../util/utilityfunctions';
import {updateAnnotationAction, HttpStatusCodes, reviewNoteStatusFilter, emptyGuid} from '../../util/uiconstants';

export default function rnhNoteDetailReducer(state = initialState.rnhNoteDetail, action) {
	switch (action.type) {
		case GET_RNH_NOTE_DETAIL: {
			const response = {...action.response, notesModalOpen: state.notesModalOpen};
			if (response?.annotations) {
				response.annotations.forEach((n) => (n.gettingReplies = action.gettingReplies));

				if (action.isGroupedByEngagement) {
					const engagements = [];
					response.annotations.forEach((n) => {
						if (!engagements.some((e) => e.id === n.engagementId)) {
							//for each engagement that is not already in the list of engagements we push the engagement and it's notes
							engagements.push({
								id: n.engagementId,
								name: n.engagementDescription,
								openNotesCount: response.annotations.filter((note) => note.engagementId === n.engagementId && note.statusId === reviewNoteStatusFilter.Open)
									.length,
								annotations: response.annotations.filter((note) => note.engagementId === n.engagementId)
							});
						}
					});

					//create engagement property and assign to the list of all engagements
					//ordered by engagement name
					response.annotations = engagements.sort((engagement1, engagement2) => engagement1.name.localeCompare(engagement2.name));
				}

				response.payload = action.payload;

				//Fix for modal counts to prevent making several API calls
				if (action.payload && action.payload.isModalRequest && response.otherEngagementsReviewNoteCount > 0) {
					response.allOpenReviewNotesCount = response.annotations.filter(
						(note) => note.engagementId === action.payload.engagementId && note.statusId === reviewNoteStatusFilter.Open
					).length;
					response.myOpenReviewNotesCount = response.annotations.filter(
						(note) =>
							note.engagementId === action.payload.engagementId &&
							(note.assignedToUserId === action.payload.currentUserId || note.createUserId === action.payload.currentUserId) &&
							note.statusId === reviewNoteStatusFilter.Open
					).length;
				}

				if (action.entityId) {
					response.entityId = action.entityId || '';
				}
			}
			return {documentIds: {...state.documentIds}, ...response};
		}
		case OPEN_RNH_NOTE_DETAIL_MODAL: {
			return {...state, notesModalOpen: action.isOpen ?? false};
		}
		case CLEAR_RNH_NOTE_DETAIL: {
			if (action.useInitial === true) {
				return {annotations: null, notesModalOpen: state.notesModalOpen, documentIds: state.documentIds};
			} else {
				return {};
			}
		}
		case SET_RNH_SELECTED_NOTE_ID_RESET_CONTEXT: {
			return {};
		}
		case GET_RNH_NOTE_REPLIES: {
			if (!state || !state.annotations) return state;

			if (action.responseObj.status === HttpStatusCodes.Ok) {
				const parentNotes = action.response;
				if (parentNotes && parentNotes.annotations) {
					const replacementsById = new Map(parentNotes.annotations.map((n) => [n.id, n]));

					const newAnnotations = action.isGroupedByEngagement
						? state.annotations.map((engagement) => {
								const updatedInner = engagement.annotations.map((a) => replacementsById.get(a.id) || a);
								return {...engagement, annotations: updatedInner};
							})
						: state.annotations.map((a) => replacementsById.get(a.id) || a);

					return {...state, annotations: newAnnotations};
				}
			}

			return state;
		}
		case POST_RNH_NEW_NOTE: {
			if (!state || !state.annotations) return state;

			if (action.responseObj.status === HttpStatusCodes.Created) {
				// Reducer is being called more than one time.
				const existingNote = state.annotations.find(({id}) => id === action.responseObj?.data?.id);
				if (existingNote) {
					return state;
				}

				const isCurrentUserInvolved = action.noteInfo.currentUser === action.noteInfo.fromUser || action.noteInfo.currentUser === action.noteInfo.toUser;

				const newAllOpen = state.allOpenReviewNotesCount + 1;

				const newNote = {
					engagementId: action.noteInfo.engagementId,
					annotationTypeId: action.noteInfo.annotationTypeId,
					assignedToUserName: action.noteInfo.toUserName,
					priorityTypeId: action.noteInfo.priorityTypeId,
					createUserName: action.noteInfo.fromUserName,
					assignedToUserId: action.noteInfo.toUser,
					createUserId: action.noteInfo.fromUser,
					entityUID: action.noteInfo.entityUID,
					entityID: action.noteInfo.entityID,
					dueDate: action.noteInfo.dueDate,
					text: action.noteInfo.comments,
					id: action.responseObj.data.id,
					statusId: action.responseObj.data.statusId,
					createDate: action.responseObj.data.createDate,
					modifiedDate: action.responseObj.data.createDate,
					referenceNumber: action.responseObj.data.referenceNumber,
					editable: action.noteInfo.currentUser === action.noteInfo.fromUser,
					recordingId: action?.noteInfo?.recordingId,
					annotationAttribute: JSON.stringify(action?.noteInfo?.recordingAttribute)
				};

				return {
					...state,
					totalCount: state.totalCount + 1,
					allOpenReviewNotesCount: newAllOpen,
					myOpenReviewNotesCount: isCurrentUserInvolved ? state.myOpenReviewNotesCount + 1 : state.myOpenReviewNotesCount,
					annotations: [...state.annotations, newNote],
					documentIds: {
						...state.documentIds,
						[state.entityId]: newAllOpen
					}
				};
			}
			return state;
		}
		case POST_RNH_NOTE_REPLY: {
			if (!state || !state.annotations) return state;
			const {response, responseObj, isEngagementComment} = action;

			if ([HttpStatusCodes.Created, HttpStatusCodes.Ok].includes(responseObj.status)) {
				const parentNote = !isEngagementComment && response.parentAnnotation;

				const newAnnotations = state.annotations.map((a) => {
					const isTarget = parentNote
						? a.id === parentNote.id
						: Boolean(response?.parentEngagementCommentGuid && a.engagementCommentGUID === response.parentEngagementCommentGuid);
					if (!isTarget) return a;

					const newReply = {
						author: isEngagementComment ? `${response.createUserFirstName} ${response.createUserLastName}` : response.createUserName,
						authorUserID: response.createUserId,
						id: isEngagementComment ? response.engagementCommentId : response.id,
						isCreatorSelf: true,
						modifiedDate: response.createDate,
						parentId: a.id,
						text: isEngagementComment ? response.commentText : response.text,
						recordingId: action?.meta?.api?.data?.recordingId,
						annotationAttribute: JSON.stringify(action?.meta?.api?.data?.recordingAttribute)
					};

					const updatedReplies = [...(a.replies || []), newReply];
					return {
						...a,
						...(isEngagementComment
							? {modifiedDate: response.parentModifyDate}
							: {
									...(parentNote.fromUser && {createUserId: parentNote.fromUser}),
									...(parentNote.fromUserName && {createUserName: parentNote.fromUserName}),
									...(parentNote.dueDate && {dueDate: parentNote.dueDate}),
									...(parentNote.toUser && {assignedToUserId: parentNote.toUser}),
									...(parentNote.toUserName && {assignedToUserName: parentNote.toUserName}),
									...(parentNote.statusId && {statusId: parentNote.statusId}),
									...(parentNote.modifiedDate && {modifiedDate: parentNote.modifiedDate}),
									...(parentNote.priorityTypeId && {priorityTypeId: parentNote.priorityTypeId})
								}),
						replies: updatedReplies,
						replyCount: updatedReplies.length
					};
				});

				return {...state, annotations: newAnnotations};
			}

			return state;
		}
		case UPDATE_RNH_NOTE_INFO: {
			if (!state || !state.annotations) return state;

			const userId = action.data.toUser;
			const userName = action.data.userFullName;
			const priorityTypeId = action.data.priorityTypeId;
			const annotationStatusId = action.data.annotationStatusId;
			const dueDate = action.data.dueDate;
			const modifiedDate = action.data.modifiedDate;

			return {
				...state,
				annotations: state.annotations.map((a) =>
					a.id !== action.selectedNoteId
						? a
						: {
								...a,
								...(userId && {assignedToUserId: userId, assignedToUserName: userName}),
								...(priorityTypeId && {priorityTypeId}),
								...(annotationStatusId && {statusId: annotationStatusId}),
								...(dueDate && {dueDate}),
								modifiedDate
							}
				)
			};
		}
		case UPDATE_RNH_NOTE_DETAIL: {
			if (!state || !state.annotations) return state;

			if (action.responseObj.status === HttpStatusCodes.Ok) {
				const newAnnotations = state.annotations.map((a) => {
					if (action.data.updateAction === updateAnnotationAction.editReply) {
						const replyMatch = a.replies?.find((r) => r.id === action.noteId);
						if (replyMatch) {
							// This is the parentNote: update the reply + apply empty updates to parent (modifiedDate)
							const updatedReplies = a.replies.map((r) => (r.id === action.noteId ? applyNoteUpdates(r, action.data, action.sendCommentData) : r));
							return applyNoteUpdates({...a, replies: updatedReplies}, {}, action.sendCommentData);
						}
						return a;
					}
					// Normal note edit
					return a.id === action.noteId ? applyNoteUpdates(a, action.data, action.sendCommentData) : a;
				});

				const newAllOpen = newAnnotations.filter(
					(item) => item.statusId === reviewNoteStatusFilter.Open && item.engagementId === action.data.engagementId
				).length;
				const newMyOpen = newAnnotations.filter(
					(item) =>
						item.statusId === reviewNoteStatusFilter.Open &&
						(item.assignedToUserId === action.currentUser || item.createUserId === action.currentUser) &&
						item.engagementId === action.data.engagementId
				).length;

				return {
					...state,
					annotations: newAnnotations,
					allOpenReviewNotesCount: newAllOpen,
					myOpenReviewNotesCount: newMyOpen,
					documentIds: {
						...state.documentIds,
						[state.entityId]: newAllOpen
					}
				};
			} else if (action.error.response.status === HttpStatusCodes.NotFound) {
				return {...state, parentWasDeleted: true};
			}

			return state;
		}
		case DELETE_RNH_NOTE_DETAIL: {
			if (!state || !state.annotations) return state;
			if (action.responseObj.status === HttpStatusCodes.NoContent || action.responseObj.status === HttpStatusCodes.Ok) {
				let foundParentWasDeleted = false;

				const newAnnotations = state.annotations.map((a) => {
					const hasReply = a.replies?.some((r) => r.id === action.data.noteId);
					if (hasReply) {
						const filteredReplies = a.replies.filter((r) => r.id !== action.data.noteId);
						return {
							...a,
							replies: filteredReplies,
							modifiedDate: action.data.modifiedDate,
							replyCount: a.replyCount - 1
						};
					}
					if (a.id === action.data.noteId) {
						foundParentWasDeleted = true;
					}
					return a;
				});

				return {
					...state,
					annotations: newAnnotations,
					...(foundParentWasDeleted && {parentWasDeleted: true})
				};
			} else if (action.error.response.status === HttpStatusCodes.Conflict) {
				// Bifurcation only for concurrency scenario when another user changes open status from parent note.
				return state;
			} else if (action.error.response.status === HttpStatusCodes.NotFound) {
				// Bifurcation only for concurrency scenario when another user deletes parent note.
				return {...state, parentWasDeleted: true};
			}
			return state;
		}
		case DELETE_RNH_PARENT_NOTE: {
			if (!state || !state.annotations) return state;
			if (
				action.responseObj.status === HttpStatusCodes.NoContent ||
				action.responseObj.status === HttpStatusCodes.Ok ||
				action.error.response.status === HttpStatusCodes.NotFound
			) {
				return {
					...state,
					annotations: state.annotations.filter((a) => a.id !== action.data.noteId),
					parentWasDeleted: true
				};
			}
			return state;
		}
		case GET_FORM_BODY_COMMENT: {
			if (action.responseObj.status === HttpStatusCodes.Ok) {
				if (!state.annotations?.[0]) return state;
				return {
					...state,
					annotations: state.annotations.map((a, i) => (i === 0 ? {...a, description: action.response} : a))
				};
			}
			return state;
		}
		case ARES_GET_DOCUMENT_BY_ID: {
			if (action.response) {
				return {
					...state,
					documentIds: {
						...state.documentIds,
						[action.response.id]: action.response.engagementReviewNoteCount
					}
				};
			}
			break;
		}
		default:
			return state;
	}
}

const getRecordingUpdates = (data) => {
	if (Object.keys(data).length === 0) return {};
	if (data.updateAction !== updateAnnotationAction.editNote && data.updateAction !== updateAnnotationAction.editReply) return {};
	if (data?.recordingId && data?.recordingId !== emptyGuid) {
		return {
			recordingId: data.recordingId,
			...(data.recordingAttribute && {annotationAttribute: JSON.stringify(data.recordingAttribute)})
		};
	}
	return {recordingId: emptyGuid, annotationAttribute: ''};
};

const applyNoteUpdates = (note, data, isEngagementComment = false) => {
	if (!note) return note;
	return {
		...note,
		...(data.toUser && {assignedToUserId: data.toUser, assignedToUserName: data.toUserName}),
		...(data.assignedToUserId && {assignedToUserId: data.assignedToUserId}),
		...(data.assignedToName && {assignedToUserName: data.assignedToName}),
		...(data.priorityTypeId && {priorityTypeId: data.priorityTypeId}),
		...(data.annotationStatusId && {
			statusId: isEngagementComment ? Utility.getAnnotationIdFromComment(data.annotationStatusId) : data.annotationStatusId
		}),
		...(data.engagementCommentStatusId && {statusId: Utility.getAnnotationIdFromComment(data.engagementCommentStatusId)}),
		...(data.dueDate && {dueDate: data.dueDate}),
		...(data.comments !== undefined && {text: data.comments}),
		...(data.commentText && {text: data.commentText}),
		...(data.statusChangeUserName && {statusChangeUserName: data.statusChangeUserName, statusChangeDate: new Date().toISOString()}),
		...(note.isCallingAPI && {isCallingAPI: false}),
		...getRecordingUpdates(data),
		modifiedDate: data.modifiedDate || new Date().toISOString()
	};
};
